import React from 'react';
import { Star, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import justSolarIcon from '../assets/icons/justsolar.svg';
import justPdfIcon from '../assets/icons/justpdf.png';
import justPrintIcon from '../assets/icons/justprint.png';
import justSaverIcon from '../assets/icons/justsaver.png';
import justConverterIcon from '../assets/icons/justconverter.png';

const Apps = () => {
  const apps = [
    {
      isProductPage: true,
      name: "JustSolar",
      category: "Enterprise Software / Solar EPC",
      price: "₹1,990 (One-Time Lifetime Access)",
      productUrl: "/justsolar",
      logoUrl: justSolarIcon,
      tagline: "The Dedicated Android EPC Suite for Solar Businesses",
      description: "Specialized commercial Android software for solar EPC companies, rooftop installers, and site survey engineers. Streamlines rooftop surveys, solar PV capacity calculations, Bill of Materials (BOM), and instant client proposals.",
      features: [
        "Solar PV capacity & string sizing calculator",
        "Rooftop shading & site survey inspection tool",
        "Automated Bill of Materials (BOM) & cost estimator",
        "Generate & export branded client PDF proposals on-site",
        "100% offline-ready for remote field sites"
      ],
      rating: "Enterprise",
      stars: "★★★★★",
      ctaText: "View JustSolar & Pricing"
    },
    {
      name: "Image to PDF - JustPDF",
      packageName: "com.justlabs.justpdf",
      category: "Productivity",
      logoUrl: justPdfIcon,
      tagline: "Instant High-Quality Image to PDF Conversion",
      description: "Transform your photos and documents into professional PDF files instantly. Advanced offline conversion engine ensures zero quality loss with lightning-fast processing, zero watermarks, and complete privacy.",
      features: [
        "Instant offline image-to-PDF conversion",
        "Batch processing for multiple images simultaneously",
        "No watermarks, page limits, or hidden fees",
        "100% on-device processing protecting privacy",
        "Material You dynamic theme integration"
      ],
      playStoreUrl: "https://play.google.com/store/apps/details?id=com.justlabs.justpdf",
      rating: "4.9",
      downloads: "10K+",
      stars: "★★★★★"
    },
    {
      name: "JustPrint - Printable Notes",
      packageName: "com.justlabs.justprint",
      category: "Productivity & Tools",
      logoUrl: justPrintIcon,
      tagline: "Distraction-Free Printable Notes & Document Utility",
      description: "Format notes, receipts, checklists, and documents for rapid printing directly from your Android phone. Optimized for standard office printers and portable thermal POS printers via WiFi and Bluetooth.",
      features: [
        "Instant document & note formatting for printing",
        "Thermal printer & standard printer compatibility",
        "Clean, distraction-free markdown note editor",
        "Quick export to PDF and physical print spooler",
        "100% offline — no cloud sync required"
      ],
      playStoreUrl: "https://play.google.com/store/apps/details?id=com.justlabs.justprint",
      rating: "4.7",
      downloads: "5K+",
      stars: "★★★★★"
    },
    {
      name: "Status Saver - JustSaver",
      packageName: "com.justlabs.justsaver",
      category: "Media & Video",
      logoUrl: justSaverIcon,
      tagline: "Secure Offline Media Saver & Gallery Manager",
      description: "Your lightweight companion for saving, previewing, and managing media content locally. Built with an offline-first approach that respects device storage and user privacy completely.",
      features: [
        "Save and organize photos & videos effortlessly",
        "Built-in HD media player and photo viewer",
        "100% offline — zero tracking or data harvesting",
        "Dark theme and customizable UI layout",
        "Fast one-tap gallery export"
      ],
      playStoreUrl: "https://play.google.com/store/apps/details?id=com.justlabs.justsaver",
      rating: "4.6",
      downloads: "20K+",
      stars: "★★★★☆"
    },
    {
      name: "JPG to PNG - Image Converter",
      packageName: "com.justlabs.justconverter",
      category: "Image Converter & Tools",
      logoUrl: justConverterIcon,
      tagline: "Batch Image Format Conversion & Optimizer",
      description: "Effortlessly convert images across popular formats (JPG, PNG, WEBP, BMP, GIF) while preserving original resolution and metadata. Fast, lossless, and completely offline.",
      features: [
        "Convert JPG, PNG, WEBP, BMP, GIF & more",
        "High-speed batch image processing",
        "Preserve original image resolution & EXIF data",
        "Lossless compression algorithms",
        "Material You design with dark mode"
      ],
      playStoreUrl: "https://play.google.com/store/apps/details?id=com.justlabs.justconverter",
      rating: "4.5",
      downloads: "15K+",
      stars: "★★★★☆"
    }
  ];

  return (
    <section id="apps" className="section apps-section">
      <div className="container">
        <div className="section-header animate-on-scroll">
          <span className="section-badge">Software Products</span>
          <h2 className="section-title">Our Android Software Product Lineup</h2>
          <p className="section-subtitle">
            Explore our ecosystem of standalone software products — from high-utility everyday consumer apps to specialized commercial enterprise tools like JustSolar.
          </p>

          {/* Official Google Play Store Developer Card */}
          <div className="dev-profile-banner">
            <div className="dev-profile-info">
              <span className="dev-profile-label">Official Google Play Developer</span>
              <strong>JustLabs on Google Play Store</strong>
              <p>Download our verified consumer apps directly on your Android phone</p>
            </div>
            <a 
              href="https://play.google.com/store/apps/dev?id=5490381095417506124&hl=en_IN" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="dev-profile-link-btn"
            >
              <span>View All Apps on Play Store</span>
              <ExternalLink size={15} />
            </a>
          </div>
        </div>
        
        <div className="apps-grid">
          {apps.map((app, index) => (
            <div key={index} className={`app-card animate-on-scroll ${app.isProductPage ? 'featured-product-card' : ''}`}>
              <div className="app-header">
                <div className="app-icon-wrapper">
                  <img 
                    src={app.logoUrl} 
                    alt={`${app.name} icon`} 
                    className="app-play-icon"
                    loading="lazy"
                  />
                </div>
                <div className="app-info">
                  <h3>{app.name}</h3>
                  <div className="app-category">{app.category}</div>
                </div>
              </div>
              
              {app.price && (
                <div className="app-card-price">
                  <strong>Price:</strong> <span>{app.price}</span>
                </div>
              )}

              <p className="app-description">{app.description}</p>
              
              <ul className="app-features">
                {app.features.map((feature, featureIndex) => (
                  <li key={featureIndex}>{feature}</li>
                ))}
              </ul>
              
              <div className="app-footer">
                {app.isProductPage ? (
                  <Link to={app.productUrl} className="google-play-btn solar-cta-btn">
                    <span>{app.ctaText || "View JustSolar & Pricing"}</span>
                    <ArrowRight size={14} />
                  </Link>
                ) : (
                  <a href={app.playStoreUrl} className="google-play-btn" target="_blank" rel="noopener noreferrer">
                    <Star size={14} />
                    <span>Get on Play Store</span>
                  </a>
                )}
                
                <div className="app-rating">
                  <div className="stars">{app.stars}</div>
                  <span>{app.rating}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Apps;